import { ModalContent, ModalFooter } from '@components/modal';
import { NprResultTypes, PcnResultTypes, RequestTypeIds } from '@src/utils/enums';
import { useQuery } from '@tanstack/react-query';
import { JPA_QUERY_KEYS, JpaQueries } from '@api/services/jpa';
import { getAppStore } from '@src/utils/store-reference';
import { resultTypeMapping } from './selfVerifyResultHelper';
import { useRequest } from '@src/stores';
import { getNonPcnOutcomeContent } from '@pages/Forms/input-forms/NonPcnForm/nonPcnHelpers';
import { getNprOutcomeContent } from '@pages/Forms/input-forms/NprForm/nprHelpers';
import { Button } from '@trussworks/react-uswds';
import { useAddNonPcnMutation } from '@api/services/non-pcn/hooks/useAddNonPcnMutation';
import { useMemo } from 'react';
import './selfVerifyResultModal.scss';
import { MarkdownRenderer } from '@src/app-components/markdown-renderer/MarkdownRenderer';

const SelfVerifyResultModal = ({ result }: { result: PcnResultTypes | NprResultTypes }) => {
  const { mutate: addNonPcn } = useAddNonPcnMutation();
  const store = getAppStore();
  const { state, district, geometry } = store.selectRequestFormDataLocation();
  const { selectedRequestId } = useRequest();
  const reqId = Number(selectedRequestId);

  // Get jpaOrgIds for the new request from NPR result
  const { data: orgData } = useQuery({
    queryKey: [JPA_QUERY_KEYS.GET_ORG, state, district, geometry, resultTypeMapping[result]],
    queryFn: () =>
      JpaQueries.getOrgs({
        state: state,
        district,
        geometry: geometry,
        requestType: resultTypeMapping[result],
      }),
    enabled: !!result && !!resultTypeMapping[result],
  });

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const outcomeContent: { [key: number]: any } = useMemo(
    () => ({
      [RequestTypeIds.NoPermitRequired]: () => getNprOutcomeContent({ orgData }), // unsure if we need to keep this -> I don't think so, but verifying...
      [RequestTypeIds.NonPreconstructionNotification]: () => getNonPcnOutcomeContent(orgData, addNonPcn),
      [RequestTypeIds.ActivityBasedNoPermitRequired]: () => getNprOutcomeContent({ orgData }),
    }),
    [addNonPcn, orgData]
  );

  if (!result) return;

  const outcomeContentFn = outcomeContent?.[reqId];
  const modalContent = outcomeContentFn(orgData)?.[result];

  return (
    <ModalContent title={modalContent?.title}>
      <div className='modal-body'>
        <MarkdownRenderer text={modalContent?.content} />
      </div>
      <div className='self-verify-result-modal__footer'>
        <ModalFooter onSave={modalContent?.onSave} saveText={modalContent?.saveText} showSaveButton showCancelButton>
          {modalContent?.canAddAnother && (
            <Button
              type='button'
              outline
              onClick={modalContent?.onAddAnother}
              className='self-verify-result-modal__secondary-action'
            >
              Try a Different NWP Type
            </Button>
          )}
        </ModalFooter>
      </div>
    </ModalContent>
  );
};

export default SelfVerifyResultModal;
